import Env from "@/config/environment";
import type { User } from "@/features/auth/hooks/use-user";
import type { Client } from "@/features/clients/types";
import posthog from "posthog-js";

/**
 * Product analytics for the back-office, in the same PostHog project as the
 * landing site so a visitor can be followed from the marketing page to signup.
 *
 * Two places never load it:
 * - `localhost`, like Sentry, so development does not pollute the numbers;
 * - the public chat (`/c/…`), which is an iframe on a *tenant's* website: its
 *   visitors are their customers, never asked for consent by us, and not users
 *   of the back-office anyway.
 *
 * What the back-office shows is tenants' customer data (names, numbers,
 * transcripts), so nothing on screen is sent as text: autocapture keeps which
 * element was clicked but not what it said, and replays mask text and inputs.
 */
const POSTHOG_KEY =
  import.meta.env.VITE_POSTHOG_KEY ||
  "phc_fP54dO1FKbpFh8FvuITAUd4N04uzbvklanuirsAgMAv";
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com";

let enabled = false;

export const initAnalytics = () => {
  const { hostname, pathname, origin } = document.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return;
  if (pathname.startsWith("/c/")) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: "2025-11-30",
    person_profiles: "identified_only",
    mask_all_text: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
  });
  posthog.register({
    app: "backoffice",
    app_version: Env.version,
    environment: origin.includes("dev") ? "development" : "production",
  });
  enabled = true;
};

export const identifyUser = (user: User) => {
  if (!enabled) return;
  posthog.identify(user.id, {
    email: user.email,
    name: user.full_name || undefined,
    role: user.role,
    lang: user.preferences?.lang,
  });
};

/** The tenant being worked on, so events can be read per company. */
export const setCurrentCompany = (client: Pick<Client, "id" | "name">) => {
  if (!enabled) return;
  posthog.group("company", client.id, { name: client.name });
};

export const resetAnalytics = () => {
  if (!enabled) return;
  posthog.reset();
};

export const captureEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => {
  if (!enabled) return;
  posthog.capture(event, properties);
};
